import React, { useState, useCallback, useEffect } from 'react';
import LoaderOverlay from '../components/LoaderOverlay';
import { showAppAlert } from '../utils/alertService';
import '../styles/formStyles.css';

const API_URL = process.env.REACT_APP_API_BASE_URL;

function ReportsDashboard() {
  const [allReportsData, setAllReportsData] = useState([]);
  const [reportsData, setReportsData] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterClassification, setFilterClassification] = useState('todos');
  const [isLoading, setIsLoading] = useState(false);
  const token = localStorage.getItem('token');
  const [sortConfig, setSortConfig] = useState({ key: 'numeric_ranking', direction: 'asc' });

  // Busca os dados do relatório da API
  const fetchReports = useCallback(async (searchStartDate = null, searchEndDate = null) => {
    setIsLoading(true);
    try {
      const companyId = localStorage.getItem('company_id');
      const body = {
        company_id: companyId,
        start_date: searchStartDate,
        end_date: searchEndDate,
      };

      const response = await fetch(`${API_URL}/clients/ranking`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const data = await response.json();
        const reports = Array.isArray(data) ? data : [];
        // Adiciona a propriedade de ranking numérico para ordenação
        const rankedReports = reports.map((report, index) => ({ ...report, numeric_ranking: index + 1 }));
        setAllReportsData(rankedReports);
        setReportsData(rankedReports);
      } else {
        const errorData = await response.json();
        showAppAlert('error', errorData.message || 'Erro ao buscar dados do relatório.');
      }
    } catch (error) {
      console.error('Erro ao buscar relatório:', error);
      showAppAlert('error', 'Ocorreu um erro de conexão ao buscar o relatório.');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Ordena os dados da tabela
  const sortData = useCallback((key, direction, data) => {
    const dataToSort = data || [];
    const sortedData = [...dataToSort].sort((a, b) => {
      let valA = a[key];
      let valB = b[key];

      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();

      if (valA < valB) return direction === 'asc' ? -1 : 1;
      if (valA > valB) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sortedData;
  }, []);

  // Aplica o filtro de classificação nos dados já carregados
  useEffect(() => {
    let filteredData = [...allReportsData];
    // Filtro por classificação
    if (filterClassification !== 'todos') {
      filteredData = allReportsData.filter(item => item.ranking === filterClassification);
    }
    // Re-aplica a ordenação atual
    const sortedData = sortData(sortConfig.key, sortConfig.direction, filteredData);
    setReportsData(sortedData);
  }, [filterClassification, allReportsData, sortConfig.key, sortConfig.direction, sortData]);

  // Dispara a busca na API com os filtros de data
  const handleSearch = () => {
    if (startDate && !endDate) {
      showAppAlert('warning', 'Por favor, preencha a data de fim.');
      return;
    }
    if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
      showAppAlert('warning', 'A data de fim não pode ser anterior à data de início.');
      return;
    }
    fetchReports(startDate, endDate);
  };

  const handleSort = (key) => {
    const newDirection = (sortConfig.key === key && sortConfig.direction === 'asc') ? 'desc' : 'asc';
    setSortConfig({ key, direction: newDirection });
    const sorted = sortData(key, newDirection, reportsData);
    setReportsData(sorted);
  };

  // Retorna o ícone de ordenação para o cabeçalho da coluna
  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    }
    return '';
  };

  // Retorna a medalha com base no ranking
  const getMedal = (index) => {
    if (index === 0) return '🥇';
    if (index === 1) return '🥈';
    if (index === 2) return '🥉';
    return index + 1;
  };

  return (
    <section className="dashboard-section">
      <LoaderOverlay isLoading={isLoading} message="Gerando relatório..." />
      <h3>Ranking de Clientes</h3>

      {/* Filtros e Ações */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px', marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
        <div className="filter-group">
          <label htmlFor="startDate">Data de Início:</label><br />
          <input
            type="date"
            id="startDate"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              if (endDate && new Date(e.target.value) > new Date(endDate)) {
                setEndDate(''); // Limpa a data de fim se a nova data de início for posterior
              }
              if (!e.target.value) {
                setEndDate(''); // Limpa a data de fim se a de início for removida
              }
            }}
            className="input-style"
          />
        </div>
        <div className="filter-group">
          <label htmlFor="endDate">Data de Fim:</label><br />
          <input type="date" id="endDate" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="input-style" disabled={!startDate} min={startDate} />
        </div>
        <div className="filter-group">
          <label htmlFor="filterClassification">Classificação:</label><br />
          <select
            id="filterClassification"
            value={filterClassification}
            onChange={(e) => setFilterClassification(e.target.value)}
            className="input-style"
          >
            <option value="todos">Todos</option>
            <option value="VIP">VIP</option>
            <option value="Habitual">Habitual</option>
            <option value="Esporádico">Esporádico</option>
          </select>
        </div>
        <div style={{ alignSelf: 'flex-end' }}>
          <button onClick={handleSearch} className="button-style" disabled={isLoading}>
            Buscar
          </button>
        </div>
        <div style={{ alignSelf: 'flex-end' }}>
        </div>
      </div>

      {/* Tabela de Resultados */}
      <table className="dashboard-table">
        <thead>
          <tr>
            <th onClick={() => handleSort('numeric_ranking')}>Ranking{getSortIcon('numeric_ranking')}</th>
            <th onClick={() => handleSort('nome')}>Nome do Cliente{getSortIcon('nome')}</th>
            <th onClick={() => handleSort('saldo_pontos')}>Saldo de Pontos{getSortIcon('saldo_pontos')}</th>
            <th onClick={() => handleSort('total_gasto')}>Total Gasto (R$){getSortIcon('total_gasto')}</th>
            <th onClick={() => handleSort('total_visitas')}>Total de Visitas{getSortIcon('total_visitas')}</th>
            <th onClick={() => handleSort('ranking')}>Classificação{getSortIcon('ranking')}</th>
          </tr>
        </thead>
        <tbody>
          {reportsData.length > 0 ? (
            reportsData.map((item, index) => (
              <tr key={item.id} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                <td>{getMedal(index)}</td>
                <td>{item.nome}</td>
                <td>{item.saldo_pontos}</td>
                <td>{item.total_gasto}</td>
                <td>{item.total_visitas}</td>
                <td>{item.ranking}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                Nenhum dado encontrado para o período selecionado.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

export default ReportsDashboard;